/**
 * PreviewStep Component
 * Final step: Comprehensive preview and test the macro before finalizing
 */

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Eye, Copy, Check, Edit3, ArrowLeft, Plus, Database, Code, Sparkles } from 'lucide-react'
import { Macro, MacroPattern } from '../../../types/macros'
import { generateMacroSyntax, previewMacroWithData } from '../../../utils/macroHelpers'
import { useToast } from '../../../components/ui/ToastProvider'

interface PreviewStepProps {
  macro: Omit<Macro, 'id'>
  onBack: () => void
  onAdvancedEdit: () => void
}

const PreviewStep: React.FC<PreviewStepProps> = ({
  macro,
  onBack,
  onAdvancedEdit
}) => {
  const [testData, setTestData] = useState<Record<string, string>>({})
  const [copiedSyntax, setCopiedSyntax] = useState(false)
  const { showSuccess } = useToast()

  const syntax = generateMacroSyntax({ ...macro, id: 'preview' })
  
  // Get all keys that might be in the macro
  const getRelevantKeys = (): string[] => {
    const keys: string[] = [macro.key]
    // Add any keys from conditional content
    if (macro.conditionalContent) {
      const matches = macro.conditionalContent.match(/{{(\w+)(\|[^}]+)?}}/g)
      if (matches) {
        matches.forEach(match => {
          const key = match.match(/{{(\w+)/)?.[1]
          if (key && !keys.includes(key)) keys.push(key)
        })
      }
    }
    if (macro.elseContent) {
      const matches = macro.elseContent.match(/{{(\w+)(\|[^}]+)?}}/g)
      if (matches) {
        matches.forEach(match => {
          const key = match.match(/{{(\w+)/)?.[1]
          if (key && !keys.includes(key)) keys.push(key)
        })
      }
    }
    return keys
  }

  const relevantKeys = getRelevantKeys()

  const handleCopySyntax = () => {
    navigator.clipboard.writeText(syntax)
    setCopiedSyntax(true)
    showSuccess('Macro syntax copied to clipboard!')
    setTimeout(() => setCopiedSyntax(false), 2000)
  }

  // Generate sample sentences based on macro key to provide context
  const generateSampleSentence = (macroOutput: string): string => {
    const keyLower = macro.key.toLowerCase()
    
    // Common greeting patterns
    if (keyLower.includes('name') || keyLower.includes('first') || keyLower.includes('last')) {
      return `Hi ${macroOutput}, welcome back to our store!`
    }
    
    // Product/item related
    if (keyLower.includes('product') || keyLower.includes('item') || keyLower.includes('title')) {
      return `Your ${macroOutput} has been added to your cart.`
    }
    
    // Price related
    if (keyLower.includes('price') || keyLower.includes('cost') || keyLower.includes('amount')) {
      return `Total amount: ${macroOutput}`
    }
    
    // Email related
    if (keyLower.includes('email')) {
      return `Confirmation sent to ${macroOutput}`
    }
    
    // Order related
    if (keyLower.includes('order') || keyLower.includes('number') || keyLower.includes('id')) {
      return `Order ${macroOutput} is being processed.`
    }
    
    // Location related
    if (keyLower.includes('city') || keyLower.includes('state') || keyLower.includes('country')) {
      return `Shipping to ${macroOutput}`
    }
    
    // Date related
    if (keyLower.includes('date') || keyLower.includes('time')) {
      return `Scheduled for ${macroOutput}`
    }
    
    // Status related
    if (keyLower.includes('status')) {
      return `Current status: ${macroOutput}`
    }
    
    // Default fallback
    return `Your ${macro.key.replace(/_/g, ' ')}: ${macroOutput}`
  }

  // Simulate macro rendering for different patterns
  const renderPatternPreview = (data: Record<string, any>): string => {
    let macroOutput = ''
    
    switch (macro.pattern) {
      case MacroPattern.SIMPLE:
        macroOutput = data[macro.key] || ''
        break

      case MacroPattern.FALLBACK:
        const fallback = macro.fallbackValue || 'Guest'
        macroOutput = data[macro.key] || fallback
        break

      case MacroPattern.CONDITIONAL:
        if (data[macro.key]) {
          macroOutput = macro.conditionalContent || data[macro.key]
        } else {
          return '' // No output for conditional when condition is false
        }
        break

      case MacroPattern.CONDITIONAL_ELSE:
        if (data[macro.key]) {
          macroOutput = macro.conditionalContent || data[macro.key]
        } else {
          macroOutput = macro.elseContent || 'Welcome!'
        }
        break

      case MacroPattern.ADVANCED:
        // Use the existing previewMacroWithData for advanced patterns
        macroOutput = previewMacroWithData({ ...macro, id: 'preview' }, data)
        break

      default:
        macroOutput = previewMacroWithData({ ...macro, id: 'preview' }, data)
        break
    }
    
    // If there's no macro output, return empty
    if (!macroOutput) return ''
    
    // Generate contextual sentence
    return generateSampleSentence(macroOutput)
  }

  // Generate test scenarios
  const testScenarios = [
    {
      name: 'With Test Data',
      data: { 
        ...testData,
        [macro.key]: testData[macro.key] || 'John'
      },
      icon: Database,
      iconColor: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    {
      name: 'Without Data',
      data: { 
        ...testData,
        [macro.key]: ''
      },
      icon: Database,
      iconColor: 'text-gray-500',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200'
    }
  ]

  const previewWith = renderPatternPreview(testScenarios[0].data)
  const previewWithout = renderPatternPreview(testScenarios[1].data)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={onBack}
          className="inline-flex items-center text-sm text-[#6E6D7A] hover:text-[#1A2B49] mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to configuration
        </button>
        
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-[#FE4215]/10 to-purple-500/10 rounded-lg">
            <Sparkles className="w-6 h-6 text-[#FE4215]" />
          </div>
          <h2 className="text-2xl font-bold text-[#1A2B49] dark:text-white">
            Preview Your Macro
          </h2>
        </div>
        <p className="text-[#6E6D7A] dark:text-gray-400">
          Comprehensive preview with live pattern testing. Test your macro with sample data to see exactly how it will behave in different scenarios.
        </p>
      </div>

      {/* Macro Syntax Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 bg-gradient-to-br from-[#FE4215]/5 to-[#233D71]/5 border-2 border-[#FE4215] rounded-lg"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Code className="w-5 h-5 text-[#FE4215]" />
            <h3 className="text-lg font-semibold text-[#1A2B49] dark:text-white">
              Generated Macro Syntax
            </h3>
          </div>
          <button
            onClick={handleCopySyntax}
            className="inline-flex items-center space-x-2 px-3 py-1.5 bg-white hover:bg-gray-50 text-[#1A2B49] rounded-md transition-colors border border-gray-300 text-sm font-medium"
          >
            {copiedSyntax ? (
              <>
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-green-600">Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>Copy Syntax</span>
              </>
            )}
          </button>
        </div>

        <div className="p-4 bg-gray-900 rounded-lg mb-3">
          <code className="text-green-400 font-mono text-sm break-all block">
            {syntax}
          </code>
        </div>

        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <span>
            <strong>Type:</strong> {macro.pattern?.replace('_', ' ').toLowerCase().replace(/\b\w/g, (l: string) => l.toUpperCase())} Pattern
          </span>
          <span>
            <strong>Key:</strong> {macro.key}
          </span>
        </div>

        {macro.description && (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <strong>Description:</strong> {macro.description}
            </p>
          </div>
        )}
      </motion.div>

      {/* Test Data Inputs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="p-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-[#1A2B49] dark:text-white">Test With Your Data</h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Enter sample values to test how your macro will behave with real data. The preview above updates automatically.
        </p>
        <div className="space-y-3">
          {relevantKeys.map((key) => (
            <div key={key} className="flex items-center gap-3">
              <label className="block text-sm font-medium text-[#1A2B49] dark:text-white min-w-[100px]">
                {key}
              </label>
              <input
                type="text"
                value={testData[key] || ''}
                onChange={(e) => setTestData({ ...testData, [key]: e.target.value })}
                placeholder={`Test value for ${key}`}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-[#FE4215] focus:border-transparent text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          ))}
        </div>
      </motion.div>

      {/* Enhanced Pattern Preview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
            <Eye className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[#1A2B49] dark:text-white">
              Live Pattern Preview
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              See how your {macro.pattern.toLowerCase().replace('_', '-')} pattern behaves with different data
            </p>
          </div>
        </div>

        {/* Pattern Info */}
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              <strong>Pattern:</strong> {macro.pattern?.replace('_', ' ').toLowerCase().replace(/\b\w/g, (l: string) => l.toUpperCase())}
            </span>
            <span className="text-gray-600 dark:text-gray-400">
              <strong>Data Key:</strong> {macro.key}
            </span>
          </div>
        </div>

        {/* Test Scenarios */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {testScenarios.map((scenario, index) => (
            <motion.div
              key={scenario.name}
              initial={{ opacity: 0, x: index === 0 ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + index * 0.1 }}
              className={`p-4 ${scenario.bgColor} rounded-lg border ${scenario.borderColor}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <scenario.icon className={`w-4 h-4 ${scenario.iconColor}`} />
                <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                  {scenario.name}
                </h4>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded border p-3 min-h-[60px] flex flex-col justify-center">
                {index === 0 ? (
                  previewWith ? (
                    <>
                      <p className="text-sm text-[#1A2B49] dark:text-white font-medium mb-1">
                        {previewWith}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Sample usage in context
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No output (condition not met)</p>
                  )
                ) : (
                  previewWithout ? (
                    <>
                      <p className="text-sm text-[#1A2B49] dark:text-white font-medium mb-1">
                        {previewWithout}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Sample usage in context
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No output (condition not met)</p>
                  )
                )}
              </div>

              {/* Show data being used */}
              <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                <span className="font-medium">Data:</span> {macro.key} = {
                  scenario.data[macro.key] ? 
                  `"${scenario.data[macro.key]}"` : 
                  <span className="italic">null</span>
                }
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pattern-specific Information */}
        {macro.pattern === MacroPattern.FALLBACK && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <strong>Fallback behavior:</strong> When "{macro.key}" is empty, shows "{macro.fallbackValue || 'Guest'}" instead
            </p>
          </div>
        )}

        {(macro.pattern === MacroPattern.CONDITIONAL || macro.pattern === MacroPattern.CONDITIONAL_ELSE) && (
          <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <p className="text-sm text-indigo-800 dark:text-indigo-300">
              <strong>Conditional behavior:</strong> Content visibility depends on whether "{macro.key}" has a value
            </p>
          </div>
        )}
      </motion.div>

      {/* Advanced Edit Option */}
      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-purple-900 mb-1">
              Need more control?
            </h3>
            <p className="text-xs text-purple-700">
              Switch to advanced mode to write custom syntax with nested patterns
            </p>
          </div>
          <button
            onClick={onAdvancedEdit}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium"
          >
            <Edit3 className="w-4 h-4" />
            <span>Advanced Edit</span>
          </button>
        </div>
      </div>

      {/* Next Steps Info */}
      <div className="p-5 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
          <Plus className="w-4 h-4 mr-2" />
          What's Next?
        </h3>
        <p className="text-sm text-blue-800">
          Click <strong>"Add to Macro List"</strong> below to save this macro. You can then:
        </p>
        <ul className="mt-2 space-y-1 text-sm text-blue-700">
          <li>• Copy the syntax to use in MomentScience dashboard</li>
          <li>• Export as SDK payload or URL parameters</li>
          <li>• Create additional macros for your message</li>
        </ul>
      </div>
    </div>
  )
}

export default PreviewStep
